import { differenceInDays } from 'date-fns';

import { expVal } from '@confluence/feature-experiments';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { ONBOARDING_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useIsNewUser } from '@confluence/targeting/entry-points/useIsNewUser.ts';
import { useSiteTraits } from '@confluence/targeting/entry-points/useSiteTraits.ts';

const FIRST_TWO_WEEKS_THRESHOLD = 14;
const FIRST_ACTIVATION_DATE_TRAIT = 'firstActivationDate';
const AUTO_PROVISIONED_TRAIT = 'is_jira_confluence_d0_auto-provisioned';

/**
 * Hook to check enrollment in the cfind-5926-upload-in-onboarding experiment.
 *
 * Eligibility criteria:
 * - User must be a founding admin (not an nth user)
 * - User must be within their first 2 weeks:
 *   - For auto-provisioned (AP) Xflow tenants: uses user-level first active date
 *   - For Land / non-AP Xflow tenants: uses tenant-level first activation date
 *
 * When all eligibility checks pass, fires a Statsig exposure event via expVal.
 *
 * @param options.skip - When true, returns early without firing exposure (useful for deferred rendering)
 * @returns {{ isEnabled: boolean, loading: boolean }}
 */
export const useCfind5926UploadInOnboarding = ({ skip }: { skip?: boolean } = {}): {
	isEnabled: boolean;
	loading: boolean;
} => {
	// User-level: check if this user's first active date is within 14 days (for AP Xflow)
	const { isNewUser, loading: isNewUserLoading } = useIsNewUser(
		{ activeDaysThreshold: FIRST_TWO_WEEKS_THRESHOLD },
		Boolean(skip),
	);

	// Tenant-level: check site traits for activation date and auto-provisioned status
	const { data: siteTraitsData, loading: isSiteTraitsLoading } = useSiteTraits();
	const siteActivationDate = siteTraitsData?.attributes?.find(
		({ name }) => name === FIRST_ACTIVATION_DATE_TRAIT,
	)?.value;
	const isSiteNewlyActivated =
		Boolean(siteActivationDate) &&
		differenceInDays(new Date(), new Date(siteActivationDate as string)) <
			FIRST_TWO_WEEKS_THRESHOLD;

	const isAutoProvisioned =
		siteTraitsData?.attributes?.find(({ name }) => name === AUTO_PROVISIONED_TRAIT)?.value ===
		'true';

	// Check if the user is the founding admin (exclude nth users)
	const { data: onboardingStateData, loading: isOnboardingStateLoading } = useGetOnboardingState(
		[ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN],
		Boolean(skip),
	);
	const { isFoundingAdmin } = deserializeState(onboardingStateData);

	// When skip is true, return without calling expVal (which fires exposure).
	// This allows consumers to defer the exposure event until the UI is actually rendered.
	// Note: hooks above must still be called unconditionally to satisfy React's rules of hooks.
	if (skip) {
		return { isEnabled: false, loading: false };
	}

	// If still loading, don't enable yet
	if (isNewUserLoading || isSiteTraitsLoading || isOnboardingStateLoading) {
		return { isEnabled: false, loading: true };
	}

	// Only target founding admins (exclude nth users)
	if (!isFoundingAdmin) {
		return { isEnabled: false, loading: false };
	}

	// Determine if the tenant/user is within the first 14 days based on tenant type:
	// - AP Xflow: use user-level date (confluence_first_active via useIsNewUser)
	// - Non-AP Xflow / Land: use tenant-level date (firstActivationDate)
	const isWithinFirstTwoWeeks = isAutoProvisioned ? isNewUser : isSiteNewlyActivated;

	if (!isWithinFirstTwoWeeks) {
		return { isEnabled: false, loading: false };
	}

	return {
		isEnabled: expVal('cfind-5926-upload-in-onboarding', 'isEnabled', false),
		loading: false,
	};
};
